import React, { useEffect, useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import CustomDataGrid from '../../../../components/ui/customDataGrid/customDataGrid.component';
import {
  JOB_STATUS_OPTIONS,
  JOB_STATUS_TRANSITION_ACTIONS_ENDPOINT,
  getJobStatusLabelKey,
} from '../../../../consts';
import styles from './tenantStatusTransitionActionsList.module.css';
import { useHasPermission } from '../../../../contexts/auth';
import { useBreadcrumbs } from '../../../../components/ui/breadcrumps/breadcrumbsContext';
import { TENANT_ROUTES } from '../../tenantConfig.routes';
import {
  equalsNumberFilterOperators,
  singleSelectFilterOperators,
} from '../../../../components/ui/customDataGrid/filterOperators';

export const TenantStatusTransitionActionsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setOverride, clearOverride } = useBreadcrumbs();
  const canView = useHasPermission("service.view_jobstatustransitionaction");

  useEffect(() => {
    const path = TENANT_ROUTES.jobStatusActions;
    setOverride(path, t('Job status actions'));

    return () => {
      clearOverride(path);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const STATUS_OPTIONS = useMemo(
    () => JOB_STATUS_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
    [t]
  );

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      filterOperators: equalsNumberFilterOperators(),
    },
    {
      field: 'previous_status',
      headerName: t('Previous status'),
      type: 'singleSelect',
      valueOptions: STATUS_OPTIONS,
      filterOperators: singleSelectFilterOperators(),
      valueGetter: (value, row) => row.previous_status,
      renderCell: (params) => {
        const labelKey = getJobStatusLabelKey(params.row.previous_status);
        return labelKey ? t(labelKey) : params.row.previous_status;
      },
    },
    {
      field: 'new_status',
      headerName: t('New status'),
      type: 'singleSelect',
      valueOptions: STATUS_OPTIONS,
      filterOperators: singleSelectFilterOperators(),
      valueGetter: (value, row) => row.new_status,
      renderCell: (params) => {
        const labelKey = getJobStatusLabelKey(params.row.new_status);
        return labelKey ? t(labelKey) : params.row.new_status;
      },
    },
    {
      field: 'required_fields',
      headerName: t('Required fields'),
      filterable: false,
      valueGetter: (value: unknown[]) => value?.length ?? 0,
    },
    {
      field: 'service_requirements',
      headerName: t('Requirements by service'),
      filterable: false,
      sortable: false,
      valueGetter: (value: unknown[]) => value?.length ?? 0,
    },
  ];

  const handleRowClick = (id: string) => {
    navigate(TENANT_ROUTES.jobStatusAction(id));
  };

  if (!canView) {
    return null;
  }

  return (
    <Box className={styles.container}>
      <CustomDataGrid
        icon={<SyncAltIcon />}
        title={t('Job status actions')}
        url={JOB_STATUS_TRANSITION_ACTIONS_ENDPOINT}
        gridSettingsKey="TENANT_STATUS_TRANSITION_ACTIONS"
        columns={columns}
        refreshEventName="statusTransitionActionsUpdated"
        onRowClick={handleRowClick}
      />
    </Box>
  );
};
